import React, { useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, X, Save, Loader2, GripVertical, Star, Calendar, Eye, EyeOff } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

import { adminListNews, adminCreateNews, adminUpdateNews, adminDeleteNews, adminReorderNews, resolveImg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import ImageUploader from '../../components/admin/ImageUploader';
import I18nField from '../../components/admin/I18nField';

// Helper: read the ES text from a value that may be a plain string or an { es, en, fr } dict.
const i18nEs = (v) => (typeof v === 'string' ? v : (v && v.es) || '');

const empty = () => ({
  date: new Date().toISOString().slice(0, 10),
  title: '',
  image: '',
  content: '',
  link: '',
  status: 'draft',
  featured: false,
  order: 0,
});

const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'blockquote'],
    ['clean'],
  ],
};

const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'align', 'link', 'blockquote'];

function SortableItem({ item, onEdit, onDelete, onToggleStatus, onToggleFeatured }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="gd-card p-4 flex gap-4 items-center">
      <button {...attributes} {...listeners} className="text-white/40 hover:text-[#c9a063] cursor-grab active:cursor-grabbing px-1" aria-label="Reordenar">
        <GripVertical size={18} />
      </button>
      <div className="w-24 h-16 bg-zinc-900 flex-shrink-0 overflow-hidden">
        {item.image ? <img src={resolveImg(item.image)} alt={item.title} className="w-full h-full object-cover" /> : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-[#c9a063] mb-1">
          <Calendar size={10} /> {item.date}
          {item.featured && <span className="inline-flex items-center gap-1 text-amber-400"><Star size={10} /> Destacada</span>}
          <span className={`ml-auto px-2 py-0.5 border text-[9px] ${item.status === 'published' ? 'border-emerald-500/40 text-emerald-400' : 'border-amber-500/40 text-amber-400'}`}>
            {item.status === 'published' ? 'Publicada' : 'Borrador'}
          </span>
        </div>
        <h3 className="font-serif-display text-lg text-white truncate">{i18nEs(item.title) || '(sin título)'}</h3>
      </div>
      <div className="flex gap-1.5 flex-shrink-0">
        <button onClick={() => onToggleFeatured(item)} title={item.featured ? 'Quitar destacada' : 'Marcar destacada'} className={`text-xs px-2 py-1.5 border ${item.featured ? 'border-amber-500/60 text-amber-400' : 'border-white/15 text-white/60 hover:text-amber-400 hover:border-amber-500/60'}`}><Star size={12} /></button>
        <button onClick={() => onToggleStatus(item)} title={item.status === 'published' ? 'Pasar a borrador' : 'Publicar'} className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-[#c9a063] hover:border-[#c9a063]">{item.status === 'published' ? <Eye size={12} /> : <EyeOff size={12} />}</button>
        <button onClick={() => onEdit(item)} className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-[#c9a063] hover:border-[#c9a063]"><Edit2 size={12} /></button>
        <button onClick={() => onDelete(item)} className="text-xs px-2 py-1.5 border border-white/15 text-white/60 hover:text-red-400 hover:border-red-400"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

export default function AdminNoticias() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }), useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }));

  const load = () => { setLoading(true); adminListNews().then(setItems).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    if (!i18nEs(editing.title).trim()) {
      toast({ title: 'Falta el titular', variant: 'destructive' });
      return;
    }
    try {
      if (editing.id) await adminUpdateNews(editing.id, editing);
      else await adminCreateNews(editing);
      toast({ title: 'Noticia guardada' });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar', variant: 'destructive' });
    }
  };

  const remove = async (item) => {
    if (!confirm(`¿Borrar la noticia "${i18nEs(item.title)}"?`)) return;
    await adminDeleteNews(item.id);
    load();
  };

  const toggleStatus = async (item) => {
    await adminUpdateNews(item.id, { status: item.status === 'published' ? 'draft' : 'published' });
    load();
  };

  const toggleFeatured = async (item) => {
    await adminUpdateNews(item.id, { featured: !item.featured });
    load();
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    const newItems = arrayMove(items, oldIndex, newIndex);
    setItems(newItems);
    try {
      await adminReorderNews(newItems.map((i) => i.id));
      toast({ title: 'Orden actualizado' });
    } catch (_) {
      load(); // revert
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h1 className="font-serif-display text-3xl text-white mb-1">Noticias</h1>
          <p className="text-white/55">Arrastra <GripVertical size={12} className="inline" /> para reordenar manualmente</p>
        </div>
        <button onClick={() => setEditing(empty())} className="gd-btn-gold px-5 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2"><Plus size={14} /> Nueva</button>
      </div>

      <div className="mt-6">
        {loading ? <Loader2 className="animate-spin text-[#c9a063]" /> : items.length === 0 ? (
          <div className="gd-card p-12 text-center text-white/40">Aún no hay noticias. Pulsa "Nueva" para crear la primera.</div>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {items.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    onEdit={(it) => setEditing({ ...it })}
                    onDelete={remove}
                    onToggleStatus={toggleStatus}
                    onToggleFeatured={toggleFeatured}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/80 z-50 overflow-y-auto">
          <div className="min-h-screen flex items-start justify-center p-4">
            <div className="bg-[#0d0d0e] border border-white/10 w-full max-w-3xl my-8">
              <div className="sticky top-0 bg-[#0d0d0e] z-10 flex items-center justify-between px-6 py-4 border-b border-white/10">
                <h2 className="font-serif-display text-2xl text-white">{editing.id ? 'Editar' : 'Nueva'} noticia</h2>
                <button onClick={() => setEditing(null)}><X size={20} className="text-white/60" /></button>
              </div>
              <div className="p-6 space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2"><Field label="Titular *"><I18nField value={editing.title} onChange={(v) => setEditing({ ...editing, title: v })} testId="news-title" placeholder="Ej: Gran éxito en el Torneo de Sherry Golf" /></Field></div>
                  <Field label="Fecha *"><input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} className={inputCls} /></Field>
                </div>

                <Field label="Imagen de portada"><ImageUploader value={editing.image} onChange={(v) => setEditing({ ...editing, image: v })} folder="general" specKey="experience_cover" /></Field>

                <div>
                  <label className={labelCls}>Texto</label>
                  <div className="news-editor bg-zinc-950 border border-white/10">
                    <ReactQuill theme="snow" value={editing.content || ''} onChange={(v) => setEditing({ ...editing, content: v })} modules={QUILL_MODULES} formats={QUILL_FORMATS} placeholder="Escribe aquí el contenido de la noticia..." />
                  </div>
                </div>

                <Field label="Enlace externo (opcional)"><input value={editing.link || ''} onChange={(e) => setEditing({ ...editing, link: e.target.value })} className={inputCls} placeholder="https://..." /></Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-3 border-t border-white/10">
                  <Field label="Estado"><select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className={inputCls}>
                    <option value="draft">Borrador (no visible en la web)</option>
                    <option value="published">Publicada (visible en la web)</option>
                  </select></Field>
                  <label className="flex items-center gap-3 cursor-pointer pt-7">
                    <input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} className="accent-[#c9a063] w-4 h-4" />
                    <span className="text-white text-sm"><Star size={13} className="inline text-amber-400 mr-1" /> Destacar (aparece arriba en grande)</span>
                  </label>
                </div>
              </div>
              <div className="sticky bottom-0 bg-[#0d0d0e] border-t border-white/10 px-6 py-4 flex justify-end gap-3">
                <button onClick={() => setEditing(null)} className="px-5 py-2.5 text-xs uppercase tracking-widest text-white/70">Cancelar</button>
                <button onClick={save} className="gd-btn-gold px-6 py-2.5 text-[11px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2"><Save size={13} /> Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = 'bg-zinc-950 border border-white/10 text-white/90 px-3 py-2 text-sm focus:border-[#c9a063] focus:outline-none w-full';
const labelCls = 'text-[10px] tracking-widest text-white/50 uppercase block mb-1.5';
const Field = ({ label, children }) => <div><label className={labelCls}>{label}</label>{children}</div>;
